import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Skull, ScrollText } from "lucide-react";

/**
 * Recent Named-Elite kills over the last 3 days. Rendered inside the Hub
 * as a scrollable ribbon so the community sees shared history at a glance.
 */
const TIER_COLOR = {
  common: "#B0ADA5",
  uncommon: "#6E8B3D",
  rare: "#4A7BC7",
  epic: "#8A2BE2",
  legendary: "#E5C34A",
  mythic: "#E33333",
};

function ago(iso) {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function EliteBountyBoard() {
  const [kills, setKills] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api.eliteBounty()
        .then(res => { if (alive) setKills(res.kills || []); })
        .catch(() => { if (alive) setKills([]); });
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return (
    <div className="panel p-3" data-testid="elite-bounty-board">
      <div className="flex items-center gap-2 mb-2">
        <ScrollText className="w-3 h-3 text-[#E5C34A]" strokeWidth={1.5} />
        <span className="font-heading text-[10px] tracking-widest uppercase text-[#E5C34A]">
          Elite Bounty · Last 3 Days
        </span>
      </div>
      {kills === null && (
        <div className="text-center text-[#666] text-xs italic py-3">Loading records…</div>
      )}
      {kills !== null && kills.length === 0 && (
        <div className="text-center text-[#666] text-xs italic py-3">
          No elites felled recently. The realm holds its breath.
        </div>
      )}
      {kills !== null && kills.length > 0 && (
        <div className="flex flex-col gap-1.5 max-h-[220px] overflow-y-auto">
          {kills.map(k => {
            const tier = k.drop?.rarity || "rare";
            const tone = TIER_COLOR[tier] || "#B0ADA5";
            return (
              <div
                key={k.kill_id}
                data-testid={`bounty-${k.kill_id}`}
                className="flex items-center gap-2 p-2 border border-[#262626] bg-[#0a0806]"
              >
                <div
                  className="w-6 h-6 flex items-center justify-center border shrink-0"
                  style={{ borderColor: tone, background: `${tone}15` }}
                >
                  <Skull className="w-3 h-3" strokeWidth={1.5} style={{ color: tone }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-xs tracking-widest uppercase text-[#E2DFD8] truncate">
                    {k.enemy_name}
                  </div>
                  <div className="font-mono text-[9px] text-[#8c8c8c] tracking-widest uppercase truncate">
                    slain by <span className="text-[#B89C3A]">{k.killer_name}</span>
                    {" "}· Lv {k.killer_level} {k.killer_class}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {k.drop?.name && (
                    <div
                      className="font-mono text-[9px] tracking-widest uppercase truncate max-w-[110px]"
                      style={{ color: tone }}
                      title={k.drop.name}
                    >
                      {k.drop.name}
                    </div>
                  )}
                  <div className="font-mono text-[9px] text-[#666] tracking-widest uppercase">
                    {ago(k.at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
